import deepmerge from 'deepmerge';
import { get, isNumber, isObject, isString, mapValues } from 'lodash';
import defaultTheme from '@cld/themes';

const resolveThemeLeaf = (theme, path, foundKeys = new Set()) => {
  const value = get(theme, path);
  if (!value || (!isString(value) && !isNumber(value))) {
    return path;
  }
  if (foundKeys.has(path)) {
    throw new Error(`Cyclic theme references: ${JSON.stringify(foundKeys)}`);
  }

  foundKeys.add(path);
  return resolveThemeLeaf(theme, value, foundKeys);
};

/**
 * It resolves theme node recursively down to leaves.
 * @param node theme node to resolve
 * @param theme whole theme object that is being used to resolve path-like values
 */
const transformThemeNode = (node, theme) => {
  if (isObject(node)) {
    const mappedNode = mapValues(node, (value, key) => transformThemeNode(node[key], theme));
    return {
      ...node, // workaround that preserves Symbol() keys (although symbol keys are not mapped). Needed for @cld/component-theme
      ...mappedNode,
    };
  }
  return resolveThemeLeaf(theme, node);
};

/**
 * Generates full theme object that can be placed directly into theme context.
 * It uses `deepmerge` library which preserves tree nodes that use Symbol as a key.
 * @param themes any number of theme overrides
 * @return deeply merged default theme and overrides, also with all string references to other parts in the theme being resolved
 * (like 'spacing.xs' -> '0.5rem')
 */
export const generateTheme = (...themes) => {
  const { components, ...theme } = deepmerge.all([defaultTheme, ...themes]);
  return { ...transformThemeNode(theme, theme), components };
};
